import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "../../common/Tooltip/Tooltip";
import {
  WATERMARK_POSITIONS,
  getWatermarkFontStack,
  measureWatermarkText,
  resolveSingleMarkPosition,
  rotatedMarkHalfExtents,
  syncMarginFromNearestEdge,
  watermarkFontSizePx,
  watermarkLogoDisplaySize,
  watermarkRotationPreservingCenter,
} from "../../../features/tools/watermark/Popup/watermarkSettings";
import "./TextFormatPositionPanel.css";

/** Local draft while an Advanced field is focused so typing is not wiped by controlled resets. */
function useFocusedDraft(committedDisplay) {
  const [draft, setDraft] = useState(committedDisplay);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(committedDisplay);
  }, [committedDisplay, focused]);

  return {
    value: focused ? draft : committedDisplay,
    focused,
    setDraft,
    onFocus: () => setFocused(true),
    onBlurEnd: () => setFocused(false),
  };
}

const PositionIcon = ({ id }) => {
  if (id === "center") {
    return <span className="tfp-position__dot" aria-hidden="true" />;
  }

  const paths = {
    top: "M9 14V4M9 4L5 8M9 4L13 8",
    bottom: "M9 4V14M9 14L5 10M9 14L13 10",
    "middle-left": "M14 9H4M4 9L8 5M4 9L8 13",
    "middle-right": "M4 9H14M14 9L10 5M14 9L10 13",
    "top-left": "M13 13L5 5M5 5H10M5 5V10",
    "top-right": "M5 13L13 5M13 5H8M13 5V10",
    "bottom-left": "M13 5L5 13M5 13H10M5 13V8",
    "bottom-right": "M5 5L13 13M13 13H8M13 13V8",
  };

  const d = paths[id];
  if (!d) {
    return <span className="tfp-position__dot" aria-hidden="true" />;
  }

  return (
    <svg
      className="tfp-position__arrow"
      width="16"
      height="16"
      viewBox="0 0 18 18"
      aria-hidden="true"
    >
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const LockIcon = ({ locked }) => (
  <svg
    className="tfp-icon tfp-icon--lock"
    viewBox="0 0 16 16"
    width="16"
    height="16"
    aria-hidden="true"
  >
    {locked ? (
      <>
        <path
          d="M4.75 7.25V5.5a3.25 3.25 0 0 1 6.5 0v1.75"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect
          x="3.5"
          y="7.25"
          width="9"
          height="6.25"
          rx="1.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ) : (
      <>
        <path
          d="M5.25 7.25V5.65a2.85 2.85 0 0 1 5.2-1.55"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect
          x="3.5"
          y="7.25"
          width="9"
          height="6.25"
          rx="1.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    )}
  </svg>
);

function readStageSize(stageSelector) {
  const el = stageSelector ? document.querySelector(stageSelector) : null;
  if (!el) return { width: 0, height: 0 };
  return { width: el.offsetWidth || 0, height: el.offsetHeight || 0 };
}

const parseNum = (raw, fallback = 0) => {
  const n = Number(String(raw ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : fallback;
};

const DisabledTip = ({ label, children }) =>
  label ? (
    <Tooltip content={label} position="top" size="wide" gap={6} flip block>
      {children}
    </Tooltip>
  ) : (
    children
  );

/**
 * Shared position / layout panel (toolbar → left overlay).
 * Hosts pass enablePlacement / enableSpacing; Tile stays outside this panel.
 */
const TextFormatPositionPanel = ({
  value = {},
  onChange = () => {},
  disabled = false,
  stageSelector = ".transform-preview__canvas",
  sizeConfig = {},
  className = "",
  positionSectionTitle = "Position",
  /** 9-grid + X/Y — off for tiled layouts */
  enablePlacement = true,
  /** Spacing slider — on for tiled layouts */
  enableSpacing = false,
  /** Show spacing control (disabled when enableSpacing is false) */
  showSpacing = true,
  /** Show margin slider */
  showMargin = true,
  /**
   * Optional controlled margin % (0–40). When set with onMarginPercentChange,
   * host owns nearest-edge / tiled margin logic.
   */
  marginPercent = null,
  onMarginPercentChange = null,
  disabledTips = {},
}) => {
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const update = () => setStageSize(readStageSize(stageSelector));
    update();
    window.addEventListener("resize", update);
    const el = stageSelector ? document.querySelector(stageSelector) : null;
    let ro = null;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, [stageSelector]);

  const positionId = value.position || "center";
  const offsetX = Number(value.offsetX) || 0;
  const offsetY = Number(value.offsetY) || 0;
  const rotation = Number(value.rotation) || 0;
  const lockAspectRatio = value.lockAspectRatio !== false;
  const isLogo = value._meta?.contentType === "logo";
  const sizeInternal = Number(value.size) || 0;
  const rawMargin = Number(value._meta?.margin ?? value.margin);
  const margin = Number.isFinite(rawMargin)
    ? Math.max(0, Math.min(0.4, rawMargin))
    : 0.08;
  const flushEdges = value._meta?.flushEdges !== false;
  const spacing = Number(value.spacing ?? 0.5);

  const placementOff = disabled || !enablePlacement;
  const spacingOff = disabled || !enableSpacing;
  const heightLocked = lockAspectRatio;
  const placementTip = !enablePlacement
    ? disabledTips.placement ||
      "Text Position is only available in Single tile. Switch Tile to Single."
    : null;
  const xyTip = !enablePlacement
    ? disabledTips.xy ||
      "X and Y are only available in Single tile. Drag the pattern on the canvas to pan, or switch Tile to Single."
    : null;
  const spacingTip = !enableSpacing
    ? disabledTips.spacing ||
      "Spacing is only available in Grid or Diamond tile. Switch Tile to Grid or Diamond."
    : null;
  const heightTip = heightLocked
    ? "Height stays proportional to Width while aspect ratio is locked."
    : null;
  const lockTip = lockAspectRatio
    ? "Aspect ratio is locked. Change Width and Height will adjust proportionately."
    : "Aspect ratio is unlocked. Width and Height can be set independently.";

  const stageW = stageSize.width || 400;
  const stageH = stageSize.height || 400;

  const markMetrics = useMemo(() => {
    if (isLogo) {
      return watermarkLogoDisplaySize(
        sizeInternal,
        stageW,
        stageH,
        Number(value._meta?.logoNaturalWidth) || 0,
        Number(value._meta?.logoNaturalHeight) || 0
      );
    }
    const fontSizePx = watermarkFontSizePx(sizeInternal, stageW, stageH);
    return measureWatermarkText(
      value._meta?.text || "Your text",
      fontSizePx,
      getWatermarkFontStack(value.fontId),
      value.fontWeight,
      {
        letterSpacing: value.letterSpacing,
        textScript: value.textScript,
      }
    );
  }, [
    isLogo,
    sizeInternal,
    stageW,
    stageH,
    value._meta?.logoNaturalWidth,
    value._meta?.logoNaturalHeight,
    value._meta?.text,
    value.fontId,
    value.fontWeight,
    value.letterSpacing,
    value.textScript,
  ]);

  const markWidth = markMetrics.width;
  const markHeight = markMetrics.height;

  const basePos = useMemo(
    () =>
      resolveSingleMarkPosition({
        position: positionId,
        margin,
        stageWidth: stageW,
        stageHeight: stageH,
        textWidth: markWidth,
        textHeight: markHeight,
        rotationDeg: rotation,
        flushEdges,
      }),
    [positionId, margin, stageW, stageH, markWidth, markHeight, rotation, flushEdges]
  );

  // Absolute canvas coords = top-left of the rotated AABB (Canva-style).
  const halfExtents = useMemo(
    () => rotatedMarkHalfExtents(markWidth, markHeight, rotation),
    [markWidth, markHeight, rotation]
  );
  const centerXPx = (basePos.x + offsetX) * stageW;
  const centerYPx = (basePos.y + offsetY) * stageH;
  const xPx = centerXPx - halfExtents.halfW;
  const yPx = centerYPx - halfExtents.halfH;
  const xDisplay = Math.abs(xPx) < 0.05 ? "0" : xPx.toFixed(1);
  const yDisplay = Math.abs(yPx) < 0.05 ? "0" : yPx.toFixed(1);
  const widthDisplay =
    Math.abs(markWidth - Math.round(markWidth)) < 0.05
      ? String(Math.round(markWidth))
      : markWidth.toFixed(1);
  const heightDisplay =
    Math.abs(markHeight - Math.round(markHeight)) < 0.05
      ? String(Math.round(markHeight))
      : markHeight.toFixed(1);

  const xField = useFocusedDraft(xDisplay);
  const yField = useFocusedDraft(yDisplay);
  const widthField = useFocusedDraft(widthDisplay);
  const heightField = useFocusedDraft(heightDisplay);

  const marginPct =
    marginPercent != null && Number.isFinite(Number(marginPercent))
      ? Math.round(Number(marginPercent))
      : Math.round(margin * 100);

  const patch = (partial) => onChange({ ...value, ...partial });

  const getMarkMetricsForStage = (w, h) => {
    if (isLogo) {
      return watermarkLogoDisplaySize(
        sizeInternal,
        w,
        h,
        Number(value._meta?.logoNaturalWidth) || 0,
        Number(value._meta?.logoNaturalHeight) || 0
      );
    }
    return measureWatermarkText(
      value._meta?.text || "Your text",
      watermarkFontSizePx(sizeInternal, w, h),
      getWatermarkFontStack(value.fontId),
      value.fontWeight,
      {
        letterSpacing: value.letterSpacing,
        textScript: value.textScript,
      }
    );
  };

  const getBaseForStage = (w, h, marginOverride = margin, positionOverride = positionId) => {
    const metrics = getMarkMetricsForStage(w, h);
    return resolveSingleMarkPosition({
      position: positionOverride,
      margin: marginOverride,
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: rotation,
      flushEdges,
    });
  };

  const setPosition = (nextId) => {
    if (placementOff) return;
    const nextMargin = nextId === "center" ? 0.08 : 0;
    const metrics = getMarkMetricsForStage(stageW, stageH);
    if (nextId === "center") {
      const base = resolveSingleMarkPosition({
        position: nextId,
        margin: nextMargin,
        stageWidth: stageW,
        stageHeight: stageH,
        textWidth: metrics.width,
        textHeight: metrics.height,
        rotationDeg: rotation,
        flushEdges,
      });
      const synced = syncMarginFromNearestEdge({
        centerX: base.x,
        centerY: base.y,
        position: nextId,
        stageWidth: stageW,
        stageHeight: stageH,
        textWidth: metrics.width,
        textHeight: metrics.height,
        rotationDeg: rotation,
        flushEdges,
      });
      patch({
        position: nextId,
        offsetX: synced.offsetX,
        offsetY: synced.offsetY,
        margin: synced.margin,
        _meta: {
          ...(value._meta || {}),
          margin: synced.margin,
        },
      });
      return;
    }
    patch({
      position: nextId,
      offsetX: 0,
      offsetY: 0,
      margin: nextMargin,
      _meta: {
        ...(value._meta || {}),
        margin: nextMargin,
      },
    });
  };

  const commitWidthPx = (raw) => {
    const nextW = parseNum(raw, markWidth);
    if (!(markWidth > 0) || !Number.isFinite(nextW) || nextW <= 0) return;
    patch({ size: sizeInternal * (nextW / markWidth) });
  };

  const commitHeightPx = (raw) => {
    if (lockAspectRatio) return;
    const nextH = parseNum(raw, markHeight);
    if (!(markHeight > 0) || !Number.isFinite(nextH) || nextH <= 0) return;
    patch({ size: sizeInternal * (nextH / markHeight) });
  };

  const commitAbsX = (raw) => {
    if (placementOff) return;
    const live = readStageSize(stageSelector);
    const w = live.width || stageW;
    const h = live.height || stageH;
    const base = getBaseForStage(w, h);
    const metrics = getMarkMetricsForStage(w, h);
    const { halfW } = rotatedMarkHalfExtents(
      metrics.width,
      metrics.height,
      rotation
    );
    const topLeftPx = parseNum(raw, (base.x + offsetX) * w - halfW);
    const centerNorm = w > 0 ? (topLeftPx + halfW) / w : base.x;
    const synced = syncMarginFromNearestEdge({
      centerX: centerNorm,
      centerY: base.y + offsetY,
      position: positionId,
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: rotation,
      flushEdges,
    });
    patch({
      offsetX: synced.offsetX,
      offsetY: synced.offsetY,
      margin: synced.margin,
      _meta: {
        ...(value._meta || {}),
        margin: synced.margin,
      },
    });
  };

  const commitAbsY = (raw) => {
    if (placementOff) return;
    const live = readStageSize(stageSelector);
    const w = live.width || stageW;
    const h = live.height || stageH;
    const base = getBaseForStage(w, h);
    const metrics = getMarkMetricsForStage(w, h);
    const { halfH } = rotatedMarkHalfExtents(
      metrics.width,
      metrics.height,
      rotation
    );
    const topLeftPx = parseNum(raw, (base.y + offsetY) * h - halfH);
    const centerNorm = h > 0 ? (topLeftPx + halfH) / h : base.y;
    const synced = syncMarginFromNearestEdge({
      centerX: base.x + offsetX,
      centerY: centerNorm,
      position: positionId,
      stageWidth: w,
      stageHeight: h,
      textWidth: metrics.width,
      textHeight: metrics.height,
      rotationDeg: rotation,
      flushEdges,
    });
    patch({
      offsetX: synced.offsetX,
      offsetY: synced.offsetY,
      margin: synced.margin,
      _meta: {
        ...(value._meta || {}),
        margin: synced.margin,
      },
    });
  };

  const commitRotation = (raw) => {
    const n = Math.max(-180, Math.min(180, parseNum(raw, rotation)));
    patch(
      watermarkRotationPreservingCenter({
        nextRotation: n,
        centerX: basePos.x + offsetX,
        centerY: basePos.y + offsetY,
        position: positionId,
        margin,
        stageWidth: stageW,
        stageHeight: stageH,
        textWidth: markWidth,
        textHeight: markHeight,
        flushEdges,
      })
    );
  };

  const setMarginPct = (pct) => {
    const next = Math.max(0, Math.min(40, Number(pct) || 0));
    if (typeof onMarginPercentChange === "function") {
      onMarginPercentChange(next);
      return;
    }
    const m = next / 100;
    patch({
      margin: m,
      _meta: {
        ...(value._meta || {}),
        margin: m,
      },
    });
  };

  const setSpacing = (n) => {
    if (spacingOff) return;
    patch({ spacing: Math.max(0, Math.min(3.96, Number(n) || 0)) });
  };

  return (
    <div className={`tfp-panel${className ? ` ${className}` : ""}`} role="group" aria-label="Position">
      <section
        className={`tfp-section${placementOff ? " is-disabled-section" : ""}`}
      >
        <h3 className="tfp-section__title">{positionSectionTitle}</h3>
        <DisabledTip label={placementTip}>
          <div
            className="tfp-position-grid"
            role="group"
            aria-label={positionSectionTitle}
            aria-disabled={placementOff}
          >
            {WATERMARK_POSITIONS.map((pos) => (
              <button
                key={pos.id}
                type="button"
                className={`tfp-position${positionId === pos.id ? " is-active" : ""}`}
                disabled={placementOff}
                aria-pressed={positionId === pos.id}
                title={placementTip || pos.label}
                onClick={() => setPosition(pos.id)}
              >
                <PositionIcon id={pos.id} />
              </button>
            ))}
          </div>
        </DisabledTip>
      </section>

      <section className="tfp-section">
        <h3 className="tfp-section__title">Advanced</h3>
        <div className="tfp-size-row">
          <label className="tfp-field tfp-field--width">
            <span className="tfp-field__label">Width</span>
            <div className="tfp-field__input-wrap">
              <input
                type="text"
                inputMode="decimal"
                className="tfp-field__input"
                value={widthField.value}
                disabled={disabled}
                onFocus={widthField.onFocus}
                onChange={(e) => widthField.setDraft(e.target.value)}
                onBlur={(e) => {
                  commitWidthPx(e.target.value);
                  widthField.onBlurEnd();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    commitWidthPx(e.currentTarget.value);
                    e.currentTarget.blur();
                  }
                }}
              />
              <span className="tfp-field__unit">px</span>
            </div>
          </label>

          <DisabledTip label={heightTip}>
            <label
              className={`tfp-field tfp-field--height${
                heightLocked ? " is-disabled-field" : ""
              }`}
            >
              <span className="tfp-field__label">Height</span>
              <div className="tfp-field__input-wrap">
                <input
                  type="text"
                  inputMode="decimal"
                  className="tfp-field__input"
                  value={heightField.value}
                  disabled={disabled || heightLocked}
                  readOnly={heightLocked}
                  title={heightTip || undefined}
                  onFocus={heightField.onFocus}
                  onChange={(e) => {
                    if (heightLocked) return;
                    heightField.setDraft(e.target.value);
                  }}
                  onBlur={(e) => {
                    commitHeightPx(e.target.value);
                    heightField.onBlurEnd();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      commitHeightPx(e.currentTarget.value);
                      e.currentTarget.blur();
                    }
                  }}
                />
                <span className="tfp-field__unit">px</span>
              </div>
            </label>
          </DisabledTip>

          <div className="tfp-ratio-lock">
            <Tooltip content={lockTip} position="top" size="wide" gap={6} flip>
              <button
                type="button"
                className={`tfp-ratio-btn${lockAspectRatio ? " is-locked" : ""}${
                  disabled ? " is-disabled" : ""
                }`}
                aria-disabled={disabled}
                aria-pressed={lockAspectRatio}
                aria-label={lockTip}
                onClick={() => {
                  if (disabled) return;
                  patch({ lockAspectRatio: !lockAspectRatio });
                }}
              >
                <LockIcon locked={lockAspectRatio} />
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="tfp-xy-row">
          <DisabledTip label={xyTip}>
            <label className={`tfp-field${placementOff ? " is-disabled-field" : ""}`}>
              <span className="tfp-field__label">X</span>
              <div className="tfp-field__input-wrap">
                <input
                  type="text"
                  inputMode="decimal"
                  className="tfp-field__input"
                  value={xField.value}
                  disabled={placementOff}
                  title={xyTip || undefined}
                  onFocus={xField.onFocus}
                  onChange={(e) => xField.setDraft(e.target.value)}
                  onBlur={(e) => {
                    commitAbsX(e.target.value);
                    xField.onBlurEnd();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      commitAbsX(e.currentTarget.value);
                      e.currentTarget.blur();
                    }
                  }}
                />
                <span className="tfp-field__unit">px</span>
              </div>
            </label>
          </DisabledTip>

          <DisabledTip label={xyTip}>
            <label className={`tfp-field${placementOff ? " is-disabled-field" : ""}`}>
              <span className="tfp-field__label">Y</span>
              <div className="tfp-field__input-wrap">
                <input
                  type="text"
                  inputMode="decimal"
                  className="tfp-field__input"
                  value={yField.value}
                  disabled={placementOff}
                  title={xyTip || undefined}
                  onFocus={yField.onFocus}
                  onChange={(e) => yField.setDraft(e.target.value)}
                  onBlur={(e) => {
                    commitAbsY(e.target.value);
                    yField.onBlurEnd();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      commitAbsY(e.currentTarget.value);
                      e.currentTarget.blur();
                    }
                  }}
                />
                <span className="tfp-field__unit">px</span>
              </div>
            </label>
          </DisabledTip>
        </div>
      </section>

      {showSpacing ? (
        <section
          className={`tfp-section tfp-section--slider${
            spacingOff ? " is-disabled-section" : ""
          }`}
        >
          <DisabledTip label={spacingTip}>
            <div className="tfp-slider-field">
              <div className="tfp-slider-field__head">
                <span className="tfp-slider-field__label">Spacing</span>
                <div className="tfp-slider-field__value">
                  <div className="tfp-slider-field__value-wrap">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="tfp-slider-field__input"
                      value={spacing.toFixed(2)}
                      disabled={spacingOff}
                      title={spacingTip || undefined}
                      aria-label="Spacing value"
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (Number.isFinite(n)) setSpacing(n);
                      }}
                    />
                  </div>
                  <span className="tfp-slider-field__suffix">x</span>
                </div>
              </div>
              <input
                type="range"
                className="tfp-slider"
                min={0}
                max={3.96}
                step={0.01}
                value={spacing}
                disabled={spacingOff}
                title={spacingTip || undefined}
                aria-label="Spacing"
                onChange={(e) => setSpacing(Number(e.target.value))}
              />
            </div>
          </DisabledTip>
        </section>
      ) : null}

      {showMargin ? (
        <section className="tfp-section tfp-section--slider">
          <div className="tfp-slider-field">
            <div className="tfp-slider-field__head">
              <span className="tfp-slider-field__label">Margin</span>
              <div className="tfp-slider-field__value">
                <div className="tfp-slider-field__value-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="tfp-slider-field__input"
                    value={marginPct}
                    disabled={disabled}
                    aria-label="Margin value"
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      if (Number.isFinite(n)) setMarginPct(n);
                    }}
                  />
                </div>
                <span className="tfp-slider-field__suffix">%</span>
              </div>
            </div>
            <input
              type="range"
              className="tfp-slider"
              min={0}
              max={40}
              step={1}
              value={marginPct}
              disabled={disabled}
              aria-label="Margin"
              onChange={(e) => setMarginPct(Number(e.target.value))}
            />
          </div>
        </section>
      ) : null}

      <section className="tfp-section tfp-section--slider">
        <div className="tfp-slider-field">
          <div className="tfp-slider-field__head">
            <span className="tfp-slider-field__label">Rotation</span>
            <div className="tfp-slider-field__value">
              <div className="tfp-slider-field__value-wrap">
                <input
                  type="text"
                  inputMode="numeric"
                  className="tfp-slider-field__input"
                  value={Math.round(rotation)}
                  disabled={disabled}
                  aria-label="Rotation value"
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) commitRotation(n);
                  }}
                />
              </div>
              <span className="tfp-slider-field__suffix">°</span>
            </div>
          </div>
          <input
            type="range"
            className="tfp-slider"
            min={-180}
            max={180}
            step={1}
            value={Math.round(rotation)}
            disabled={disabled}
            aria-label="Rotation"
            onChange={(e) => commitRotation(Number(e.target.value))}
          />
        </div>
      </section>
    </div>
  );
};

export default TextFormatPositionPanel;
